import React, { useState, useEffect } from 'react'
import Swal from 'sweetalert2'
import apiClient, { BASE_URL } from '../../config/api'

const ManageDiscounts = () => {
  const [discounts, setDiscounts] = useState([])
  const [filteredDiscounts, setFilteredDiscounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    search: '',
    status: '' // 'all', 'active', 'inactive', 'expired'
  })

  const fetchDiscounts = async () => {
    try {
      setLoading(true)
      // Use placeholder endpoint - update when backend is ready
      const DISCOUNTS_ENDPOINT = `${BASE_URL}/api/sales/discounts/`
      const response = await apiClient.get(DISCOUNTS_ENDPOINT)
      setDiscounts(response.data || [])
      setFilteredDiscounts(response.data || [])
    } catch (error) {
      console.error('Error fetching discounts:', error)
      // If endpoint doesn't exist yet, show empty state
      if (error.response?.status === 404) {
        setDiscounts([])
        setFilteredDiscounts([])
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response?.data?.detail || 'Failed to fetch discounts. Please try again.'
        })
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDiscounts()
  }, [])

  useEffect(() => {
    let filtered = [...discounts]

    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(discount =>
        discount.name?.toLowerCase().includes(searchLower) ||
        discount.description?.toLowerCase().includes(searchLower)
      )
    }

    if (filters.status && filters.status !== 'all') {
      const now = new Date()
      filtered = filtered.filter(discount => {
        if (filters.status === 'active') {
          if (!discount.is_active) return false
          if (discount.start_date && new Date(discount.start_date) > now) return false
          if (discount.end_date && new Date(discount.end_date) < now) return false
          return true
        }
        if (filters.status === 'inactive') {
          return !discount.is_active
        }
        if (filters.status === 'expired') {
          return discount.end_date && new Date(discount.end_date) < now
        }
        return true
      })
    }

    setFilteredDiscounts(filtered)
  }, [discounts, filters])

  const formatCurrency = (value) => {
    const parsed = parseFloat(value)
    if (Number.isNaN(parsed)) {
      return '-'
    }
    return parsed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getDiscountStatus = (discount) => {
    if (!discount.is_active) return { text: 'Inactive', class: 'badge bg-secondary' }
    
    const now = new Date()
    if (discount.start_date && new Date(discount.start_date) > now) {
      return { text: 'Scheduled', class: 'badge bg-info' }
    }
    if (discount.end_date && new Date(discount.end_date) < now) {
      return { text: 'Expired', class: 'badge bg-danger' }
    }
    return { text: 'Active', class: 'badge bg-success' }
  }

  const handleEdit = async (discount) => {
    // Escape HTML to prevent XSS
    const escapedName = (discount.name || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
    const escapedValue = (discount.discount_value || '').toString()
    const escapedDescription = (discount.description || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
    
    const { value: formValues } = await Swal.fire({
      title: 'Edit Discount',
      html: `
        <input id="swal-input1" class="swal2-input" placeholder="Discount Name" value="${escapedName}" required>
        <select id="swal-input2" class="swal2-input">
          <option value="percentage" ${discount.discount_type === 'percentage' ? 'selected' : ''}>Percentage (%)</option>
          <option value="fixed" ${discount.discount_type === 'fixed' ? 'selected' : ''}>Fixed Amount</option>
        </select>
        <input id="swal-input3" class="swal2-input" type="number" placeholder="Discount Value" value="${escapedValue}" step="0.01" min="0" required>
        <textarea id="swal-input4" class="swal2-input" placeholder="Description (optional)">${escapedDescription}</textarea>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Update',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      preConfirm: () => {
        const name = document.getElementById('swal-input1').value.trim()
        const discountType = document.getElementById('swal-input2').value
        const value = parseFloat(document.getElementById('swal-input3').value)
        const description = document.getElementById('swal-input4').value.trim()
        
        if (!name) {
          Swal.showValidationMessage('Discount name is required')
          return false
        }
        if (isNaN(value) || value <= 0) {
          Swal.showValidationMessage('Discount value must be a positive number')
          return false
        }
        if (discountType === 'percentage' && value > 100) {
          Swal.showValidationMessage('Percentage discount cannot exceed 100%')
          return false
        }
        
        return { name, discount_type: discountType, value, description }
      },
      didOpen: () => {
        document.getElementById('swal-input1').focus()
      }
    })

    if (formValues) {
      try {
        const payload = {
          name: formValues.name,
          discount_type: formValues.discount_type,
          discount_value: formValues.value,
          description: formValues.description || null
        }

        const DISCOUNTS_ENDPOINT = `${BASE_URL}/api/sales/discounts/`
        await apiClient.put(`${DISCOUNTS_ENDPOINT}${discount.id}/`, payload)

        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Discount updated successfully!'
        })

        fetchDiscounts() // Refresh the list
      } catch (error) {
        console.error('Error updating discount:', error)
        const errorMessage = error.response?.data?.name?.[0] || 
                            error.response?.data?.value?.[0] ||
                            error.response?.data?.non_field_errors?.[0] ||
                            error.response?.data?.detail ||
                            error.response?.data?.message ||
                            'Failed to update discount. Please try again.'
        
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage
        })
      }
    }
  }

  const handleToggleActive = async (discount) => {
    try {
      const DISCOUNTS_ENDPOINT = `${BASE_URL}/api/sales/discounts/`
      await apiClient.patch(`${DISCOUNTS_ENDPOINT}${discount.id}/`, {
        is_active: !discount.is_active
      })

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: `Discount ${!discount.is_active ? 'activated' : 'deactivated'} successfully!`,
        timer: 2000,
        showConfirmButton: false
      })

      fetchDiscounts() // Refresh the list
    } catch (error) {
      console.error('Error toggling discount status:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.detail || 'Failed to update discount status. Please try again.'
      })
    }
  }

  const handleDelete = async (discountId) => {
    const discount = discounts.find(d => d.id === discountId)
    
    // Escape HTML to prevent XSS
    const escapedName = discount?.name?.replace(/"/g, '&quot;').replace(/'/g, '&#39;') || ''
    
    const { value: typedName } = await Swal.fire({
      title: 'Delete Discount',
      html: `
        <p>Are you sure you want to delete this discount?</p>
        <p class="text-danger"><strong>This action cannot be undone!</strong></p>
        <p>Type <strong>"${escapedName}"</strong> to confirm:</p>
        <input id="swal-input" class="swal2-input" placeholder="Type discount name">
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      allowOutsideClick: false,
      allowEscapeKey: true,
      didOpen: () => {
        const input = document.getElementById('swal-input')
        const confirmButton = Swal.getConfirmButton()
        
        // Initially disable the confirm button
        confirmButton.disabled = true
        confirmButton.style.opacity = '0.5'
        confirmButton.style.cursor = 'not-allowed'
        
        // Add input event listener to enable/disable button based on match
        input.addEventListener('input', () => {
          const inputValue = input.value.trim()
          const matches = inputValue === discount?.name
          
          if (matches) {
            confirmButton.disabled = false
            confirmButton.style.opacity = '1'
            confirmButton.style.cursor = 'pointer'
          } else {
            confirmButton.disabled = true
            confirmButton.style.opacity = '0.5'
            confirmButton.style.cursor = 'not-allowed'
          }
        })
        
        input.focus()
      },
      preConfirm: () => {
        const input = document.getElementById('swal-input')
        const inputValue = input.value.trim()
        
        if (!inputValue) {
          Swal.showValidationMessage('You need to type the discount name!')
          return false
        }
        
        if (inputValue !== discount?.name) {
          Swal.showValidationMessage('The name does not match!')
          return false
        }
        
        return inputValue
      }
    })

    if (typedName === discount?.name) {
      try {
        const DISCOUNTS_ENDPOINT = `${BASE_URL}/api/sales/discounts/`
        await apiClient.delete(`${DISCOUNTS_ENDPOINT}${discountId}/`)
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Discount has been deleted.',
          timer: 2000,
          showConfirmButton: false
        })
        fetchDiscounts() // Refresh the list
      } catch (error) {
        console.error('Error deleting discount:', error)
        const errorMessage = error.response?.data?.detail ||
                            error.response?.data?.message ||
                            'Failed to delete discount. Please try again.'
        
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage
        })
      }
    }
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Manage Discounts</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">Sale</li>
            <li className="breadcrumb-item active">Manage Discounts</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">
                  <i className="bi bi-percent me-2"></i>
                  Manage Discounts
                </h5>

                {/* Filters */}
                <div className="row mb-3">
                  <div className="col-md-4">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search discounts..."
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    />
                  </div>
                  <div className="col-md-3">
                    <select
                      className="form-select"
                      value={filters.status}
                      onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="expired">Expired</option>
                    </select>
                  </div>
                </div>

                {/* Discounts Table */}
                {loading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Code</th>
                          <th>Type</th>
                          <th>Value</th>
                          <th>Applicable To</th>
                          <th>Start Date</th>
                          <th>End Date</th>
                          <th>Status</th>
                          <th className="text-end" style={{ width: '200px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDiscounts.length === 0 ? (
                          <tr>
                            <td colSpan="9" className="text-center py-4">
                              <p className="text-muted mb-0">No discounts found</p>
                            </td>
                          </tr>
                        ) : (
                          filteredDiscounts.map(discount => {
                            const status = getDiscountStatus(discount)
                            return (
                              <tr key={discount.id}>
                                <td className="align-middle">
                                  <strong>{discount.name}</strong>
                                  {discount.description && (
                                    <>
                                      <br />
                                      <small className="text-muted">{discount.description}</small>
                                    </>
                                  )}
                                </td>
                                <td className="align-middle">
                                  {discount.code ? (
                                    <span className="badge bg-primary">{discount.code}</span>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                                <td className="align-middle">
                                  <span className="badge bg-info">
                                    {discount.discount_type === 'percentage' ? 'Percentage' : 'Fixed'}
                                  </span>
                                </td>
                                <td className="align-middle">
                                  {discount.discount_type === 'percentage' 
                                    ? `${discount.discount_value}%` 
                                    : formatCurrency(discount.discount_value)}
                                </td>
                                <td className="align-middle">
                                  <div>
                                    <span className="badge bg-secondary">
                                      {discount.apply_to === 'all' ? 'All Products' :
                                       discount.apply_to === 'min_purchase' ? 'Minimum Purchase' :
                                       discount.apply_to === 'product' ? 'Specific Product' :
                                       discount.apply_to === 'category' ? 'Specific Category' :
                                       'N/A'}
                                    </span>
                                    {discount.min_purchase_amount && (
                                      <>
                                        <br />
                                        <small className="text-muted">Min: {formatCurrency(discount.min_purchase_amount)}</small>
                                      </>
                                    )}
                                    {discount.max_discount_amount && parseFloat(discount.max_discount_amount) > 0 && (
                                      <>
                                        <br />
                                        <small className="text-muted">Max: {formatCurrency(discount.max_discount_amount)}</small>
                                      </>
                                    )}
                                    {discount.usage_limit && (
                                      <>
                                        <br />
                                        <small className="text-muted">Usage: {discount.usage_count || 0}/{discount.usage_limit}</small>
                                      </>
                                    )}
                                  </div>
                                </td>
                                <td className="align-middle">
                                  <small>{formatDate(discount.start_date)}</small>
                                </td>
                                <td className="align-middle">
                                  <small>{formatDate(discount.end_date)}</small>
                                </td>
                                <td className="align-middle">
                                  <span className={status.class}>{status.text}</span>
                                </td>
                                <td className="align-middle text-end">
                                  <div className="btn-group" role="group">
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => handleEdit(discount)}
                                      title="Edit discount"
                                    >
                                      <i className="bi bi-pencil-fill"></i>
                                      <span className="ms-1 d-none d-md-inline">Edit</span>
                                    </button>
                                    <button
                                      className={`btn btn-sm ${discount.is_active ? 'btn-outline-warning' : 'btn-outline-success'}`}
                                      onClick={() => handleToggleActive(discount)}
                                      title={discount.is_active ? 'Deactivate discount' : 'Activate discount'}
                                    >
                                      <i className={`bi ${discount.is_active ? 'bi-pause-fill' : 'bi-play-fill'}`}></i>
                                      <span className="ms-1 d-none d-md-inline">
                                        {discount.is_active ? 'Deactivate' : 'Activate'}
                                      </span>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => handleDelete(discount.id)}
                                      title="Delete discount"
                                    >
                                      <i className="bi bi-trash-fill"></i>
                                      <span className="ms-1 d-none d-md-inline">Delete</span>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default ManageDiscounts

